// Argus — SystemInfoWindow: recharts LineChart 3 lines (CPU% + RAM MB + Disk free GB).
// Fetch agent_metrics interval default 60s ultimele 60 minute. Refresh la 30s.

import { useEffect, useRef, useState } from 'react';
import {
  Box, FormControl, InputLabel, Select, MenuItem, Alert, CircularProgress, Typography, Card,
} from '@mui/material';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from 'recharts';
import { fetchAgentMetrics, type MetricsBucket } from '../lib/agentSettings';
import { dayjs } from '../lib/dayjsSetup';

type Range = '1h' | '6h' | '24h';
const RANGE_TO_HOURS: Record<Range, number> = { '1h': 1, '6h': 6, '24h': 24 };
const RANGE_TO_INTERVAL: Record<Range, number> = { '1h': 60, '6h': 300, '24h': 1800 };

const RANGE_STORAGE_KEY = (agentId: string) => `argus.systemInfo.range.${agentId}`;

function loadRange(agentId: string): Range {
  try {
    const stored = localStorage.getItem(RANGE_STORAGE_KEY(agentId));
    if (stored === '1h' || stored === '6h' || stored === '24h') return stored;
  } catch { /* ignore */ }
  return '1h';
}

export interface SystemInfoWindowProps {
  agentId: string;
}

export function SystemInfoWindow({ agentId }: SystemInfoWindowProps): JSX.Element {
  const [range, setRange] = useState<Range>(() => loadRange(agentId));

  useEffect(() => {
    try { localStorage.setItem(RANGE_STORAGE_KEY(agentId), range); } catch { /* ignore */ }
  }, [agentId, range]);
  const [items, setItems] = useState<MetricsBucket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const hours = RANGE_TO_HOURS[range];
        const toIso = new Date().toISOString();
        const fromIso = new Date(Date.now() - hours * 3600_000).toISOString();
        const r = await fetchAgentMetrics(agentId, { interval: RANGE_TO_INTERVAL[range], fromIso, toIso });
        if (!cancelled) {
          setItems(r.items);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Metrics error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    setLoading(true);
    void load();
    const handle = window.setInterval(() => void load(), 30_000);
    return () => { cancelled = true; clearInterval(handle); };
  }, [agentId, range]);

  const chartData = items.map((b) => ({
    time: dayjs(b.bucket).format('HH:mm'),
    bucketRaw: b.bucket,
    cpu: b.cpuPct ?? null,
    ramGb: b.ramUsedMb !== null ? Math.round(b.ramUsedMb / 1024 * 10) / 10 : null,
    disk: b.diskFreeGb,
  }));
  const last = items[items.length - 1];

  return (
    <Box data-role="systeminfo-panel" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box data-role="systeminfo-toolbar" sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, p: 1, borderBottom: 1, borderColor: 'divider' }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Range</InputLabel>
          <Select label="Range" value={range} onChange={(e) => setRange(e.target.value as Range)}>
            <MenuItem value="1h">Last hour</MenuItem>
            <MenuItem value="6h">Last 6 hours</MenuItem>
            <MenuItem value="24h">Last 24 hours</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary">{items.length} points</Typography>
      </Box>

      {error ? <Alert severity="error" sx={{ borderRadius: 0 }}>{error}</Alert> : null}

      {loading && items.length === 0 ? (
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={28} />
        </Box>
      ) : items.length === 0 ? (
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            No metrics data yet. Agent heartbeat populates a rolling 24h window.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ flexGrow: 1, p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {last ? (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
              <Card sx={{ p: 1, flexGrow: 1 }}>
                <Typography variant="caption" color="text.secondary">CPU</Typography>
                <Typography variant="h5" sx={{ color: '#3B82F6' }}>{last.cpuPct !== null ? `${Math.round(last.cpuPct)}%` : '—'}</Typography>
              </Card>
              <Card sx={{ p: 1, flexGrow: 1 }}>
                <Typography variant="caption" color="text.secondary">RAM</Typography>
                <Typography variant="h5" sx={{ color: '#A855F7' }}>{last.ramUsedMb !== null ? `${(last.ramUsedMb / 1024).toFixed(1)} GB` : '—'}</Typography>
              </Card>
              <Card sx={{ p: 1, flexGrow: 1 }}>
                <Typography variant="caption" color="text.secondary">Disk free</Typography>
                <Typography variant="h5" sx={{ color: '#10B981' }}>{last.diskFreeGb !== null ? `${last.diskFreeGb} GB` : '—'}</Typography>
              </Card>
            </Box>
          ) : null}

          <ChartContainer chartData={chartData} />
        </Box>
      )}
    </Box>
  );
}

// ResizeObserver-driven chart wrapper. Recharts ResponsiveContainer NU funcționează corect când
// părinții folosesc flex/overflow:hidden lanț — width=-1 height=-1 errors. ResizeObserver pe
// containerul nostru ne dă dimensiuni concrete (numbers) care merg garantat cu LineChart direct.
interface ChartContainerProps {
  chartData: ReadonlyArray<{ time: string; cpu: number | null; ramGb: number | null; disk: number | null }>;
}

function ChartContainer({ chartData }: ChartContainerProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (el === null) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setSize({ width: Math.floor(width), height: Math.floor(height) });
      }
    });
    ro.observe(el);
    // Fire initial measure (RO doesn't fire initial in some browsers).
    const rect = el.getBoundingClientRect();
    setSize({ width: Math.floor(rect.width), height: Math.floor(rect.height) });
    return () => ro.disconnect();
  }, []);

  return (
    <Box ref={containerRef} sx={{ flexGrow: 1, minHeight: 320, width: '100%', overflow: 'hidden' }}>
      {size.width > 0 && size.height > 0 ? (
        <LineChart width={size.width} height={size.height} data={[...chartData]} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
          <XAxis dataKey="time" fontSize={11} />
          <YAxis yAxisId="left" fontSize={11} domain={[0, 100]} />
          <YAxis yAxisId="right" orientation="right" fontSize={11} />
          <Tooltip />
          <Legend />
          <Line yAxisId="left" type="monotone" dataKey="cpu" name="CPU %" stroke="#3B82F6" dot={false} strokeWidth={2} connectNulls />
          <Line yAxisId="right" type="monotone" dataKey="ramGb" name="RAM GB" stroke="#A855F7" dot={false} strokeWidth={2} connectNulls />
          <Line yAxisId="right" type="monotone" dataKey="disk" name="Disk free GB" stroke="#10B981" dot={false} strokeWidth={2} connectNulls />
        </LineChart>
      ) : null}
    </Box>
  );
}
